import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SharedItem, SharedItemType } from '../../types/shared-items';
import IconSelect from './IconSelect';

interface ItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: SharedItem | null;
  type: SharedItemType;
  onSave: () => void;
}

const ItemModal: React.FC<ItemModalProps> = ({ isOpen, onClose, item, type, onSave }) => {
  const [formData, setFormData] = useState({
    icon: '',
    title: '',
    description: ''
  });

  useEffect(() => {
    if (item) {
      setFormData({
        icon: item.icon,
        title: item.title,
        description: item.description
      });
    } else {
      setFormData({
        icon: '',
        title: '',
        description: ''
      });
    }
  }, [item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        type
      };

      if (item) {
        const { error } = await supabase
          .from('shared_items')
          .update(data)
          .eq('id', item.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('shared_items')
          .insert([data]);

        if (error) throw error;
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving item:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-[#1F2B1F] rounded-xl w-full max-w-lg">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="text-xl font-semibold">
            {item ? 'Edit Item' : 'Add New Item'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Icon
            </label>
            <IconSelect
              value={formData.icon}
              onChange={(value) => setFormData({ ...formData, icon: value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
              required
            />
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors"
            >
              {item ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ItemModal;